import { useTranslation } from "react-i18next";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { OrganisationTrendResponse } from "../../api/organisationReporting";
import { ChartDefs, PulseDot } from "../charts/chartEffects";
import { getVendorColor } from "../charts/chartColors";
import { ChartTooltip } from "../charts/ChartTooltip";
import { SeriesLegend, type SeriesLegendEntry } from "../charts/SeriesLegend";
import { useChartEffectIds } from "../charts/useChartEffectIds";
import { useSeriesVisibility } from "../charts/useSeriesVisibility";
import panelStyles from "../consumption/ConsumptionSummary.module.css";
import { formatCount } from "../consumption/formatters";
import { coverageStarts, monthLabel } from "./overviewFigures";
import styles from "./Overview.module.css";

/** The key of the anyone-at-all series, which no vendor's id can be. */
const anyoneKey = "\u0000anyone";

/** The anyone-at-all series is a total, not a tool, so it takes the text colour, never one a tool could have. */
const anyoneColour = "var(--color-text-primary)";

/**
 * People using AI, month by month: everyone who used any tool as a glowing area, each tool's users as a line over it, a pulse on
 * the latest month, and a mark where each tool's use begins to be recorded. People are counted from what they did, so the lines
 * do not add up to the area: one person using two tools is in two lines and once in the area.
 *
 * @param props.trend - The trend.
 */
export function PeopleTrendPanel({ trend }: { trend: OrganisationTrendResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const { isHidden, toggle } = useSeriesVisibility();
  const effects = useChartEffectIds();

  const vendors = trend.vendors.filter((vendor) => trend.months.some((month) => (month.vendors.find((v) => v.vendorId === vendor.vendorId)?.activePeople ?? 0) > 0));
  const colours = new Map(vendors.map((vendor, index) => [vendor.vendorId, getVendorColor(vendor.vendorId, index)]));
  const labels = new Map(trend.months.map((month, index) => [month.periodStart, monthLabel(month.periodStart, index === 0 || month.periodStart.endsWith("-01-01"))]));
  const data = trend.months.map((month) => {
    const point: Record<string, number | string> = { name: labels.get(month.periodStart) ?? month.periodStart, [anyoneKey]: month.activePeople };
    for (const vendor of vendors) {
      point[vendor.vendorId] = month.vendors.find((v) => v.vendorId === vendor.vendorId)?.activePeople ?? 0;
    }
    return point;
  });
  const latest = data[data.length - 1] as Record<string, number | string> | undefined;
  const legend: SeriesLegendEntry[] = [
    { key: anyoneKey, label: t("overview.people.total"), color: anyoneColour },
    ...vendors.map((vendor) => ({ key: vendor.vendorId, label: vendor.displayName, color: colours.get(vendor.vendorId) ?? "", shape: "line" as const })),
  ];
  const starts = coverageStarts(trend, "activity");

  return (
    <section className={panelStyles.forecastPanel} data-spotlight aria-labelledby="overview-people-heading">
      <h2 id="overview-people-heading" className={`panel-title ${panelStyles.panelTitle}`}>
        {t("overview.people.title")}
      </h2>
      <p className={styles.subtitle}>{t("overview.people.subtitle")}</p>
      <div className={panelStyles.chartContainer} role="img" aria-label={t("overview.people.chartLabel")}>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 24, right: 16, bottom: 8, left: 0 }}>
            <ChartDefs ids={effects} gradients={[{ key: "anyone", color: anyoneColour, top: 0.22, bottom: 0 }]} />
            <CartesianGrid vertical={false} stroke="color-mix(in srgb, var(--color-border-default) 70%, transparent)" strokeDasharray="4 4" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} />
            <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} width={44} />
            <Tooltip
              content={
                <ChartTooltip
                  colors={new Map(legend.map((entry) => [entry.key, entry.color]))}
                  formatLabel={(label) => label}
                  formatValue={(value) => formatCount(value)}
                />
              }
              cursor={{ stroke: "color-mix(in srgb, var(--color-text-secondary) 40%, transparent)" }}
            />
            <Area
              type="monotone"
              dataKey={anyoneKey}
              name={t("overview.people.total")}
              stroke={anyoneColour}
              strokeWidth={3}
              fill={`url(#${effects.gradient("anyone")})`}
              filter={`url(#${effects.glow})`}
              dot={false}
              hide={isHidden(anyoneKey)}
            />
            {vendors.map((vendor) => (
              <Line
                key={vendor.vendorId}
                type="monotone"
                dataKey={vendor.vendorId}
                name={vendor.displayName}
                stroke={colours.get(vendor.vendorId)}
                strokeWidth={2}
                strokeLinecap="round"
                filter={`url(#${effects.glow})`}
                dot={false}
                hide={isHidden(vendor.vendorId)}
              />
            ))}
            {starts.map((start) => (
              <ReferenceLine
                key={start.vendorId}
                x={labels.get(start.periodStart)}
                stroke={colours.get(start.vendorId) ?? "var(--color-text-secondary)"}
                strokeDasharray="3 4"
                strokeOpacity={0.7}
              />
            ))}
            {latest && !isHidden(anyoneKey) && <ReferenceDot x={latest.name} y={latest[anyoneKey]} fill={anyoneColour} shape={PulseDot} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <SeriesLegend entries={legend} isHidden={isHidden} onToggle={toggle} />
      {starts.length > 0 && (
        <p className={styles.note}>
          {t("overview.people.coverage", {
            list: starts.map((start) => t("overview.spend.coverageItem", { tool: start.displayName, month: monthLabel(start.periodStart, true) })).join(", "),
          })}
        </p>
      )}
    </section>
  );
}
